const hasSpace = (str) => /\s/.test(str);
const hasEightCharacters = (str) => /.{8,}/.test(str);
const hasUpperCase = (str) => /[A-Z]/.test(str);
const hasLowerCase = (str) => /[a-z]/.test(str);
const hasNumber = (str) => /\d/.test(str);
const hasSpecialCharacter = (str) => /['!"#$%&()*,./:;?@`\\[\]{|}~+<=>^_-]/.test(str);

/**
 * Check if the password meets the minimum length.
 * @param {string} password
 * @param {string} lang
 * @returns {{passed: boolean, message: string}}
 */
function checkLength(password, lang) {
  const text = lang === "ENG" ? "The minimum password length is 8 characters" : "ความยาวไม่น้อยกว่า 8 ตัวอักษร";
  const passed = hasEightCharacters(password);
  const icon = passed ? "&#x2714;" : "&#x274C;";
  const msg = `<span style="filter: brightness(0) invert(1);">${icon} ${text}</span><br/>`;
  return { passed, message: msg };
}

/**
 * Check if the password contains spaces.
 * @param {string} password
 * @param {string} lang
 * @returns {{passed: boolean, message: string}}
 */
function checkSpace(password, lang) {
  const text = lang === "ENG" ? "Must not contain spaces" : "ไม่มีเว้นวรรค";
  const passed = !hasSpace(password);
  const icon = passed ? "&#x2714;" : "&#x274C;";
  const msg = `<span style="filter: brightness(0) invert(1);">${icon} ${text}</span><br/>`;
  return { passed, message: msg };
}

/**
 * Check complexity requirements: at least 3 of 4 conditions must be met.
 * Conditions:
 * 1) Upper Case
 * 2) Lower Case
 * 3) Numerals
 * 4) Special Characters
 * @param {string} password
 * @param {string} lang
 * @returns {{passed: boolean, message: string}}
 */
function checkComplexity(password, lang) {
  const criteria = [
    {
      number: 1,
      eng_text: "upper case (A , Z)",
      thai_text: "ตัวอักษรพิมพ์ใหญ่ (A , Z)",
      check: hasUpperCase(password),
    },
    {
      number: 2,
      eng_text: "lower case (a , z)",
      thai_text: "ตัวอักษรพิมพ์เล็ก (a , z)",
      check: hasLowerCase(password),
    },
    {
      number: 3,
      eng_text: "numerals (0 , 9)",
      thai_text: "ตัวเลข (0 , 9)",
      check: hasNumber(password),
    },
    {
      number: 4,
      eng_text: "special characters (such as % @ #)",
      thai_text: "อักขระพิเศษ (เช่น % @ #)",
      check: hasSpecialCharacter(password),
    },
  ];

  const passedCount = criteria.filter((c) => c.check).length;
  const passed = passedCount >= 3;

  let message = "";
  const dot = "&#x2022";
  const space = "&#x20";
  const checkMark = "&#x2714; ";
  const crossMark = "&#x274C; ";

  const headingText =
    lang === "ENG" ? `${dot} ${space} Contain at least 3 out of 4 following characteristics:` : `${dot} ${space} มีคุณสมบัติดังต่อไปนี้อย่างน้อย 3 ใน 4 อย่าง:`;

  message += `<span style="color: #ffffff;"><b>${headingText}</b></span><br/>`;

  // If passed, show checks for all that are correct,
  // no need to show fails since we met the requirement.
  if (passed) {
    criteria.forEach((c) => {
      if (c.check) {
        message += `<span style="filter: brightness(0) invert(1);">${checkMark} ${lang === "ENG" ? c.eng_text : c.thai_text}</span><br/>`;
      }
    });
  } else {
    // Not passed - show which conditions are missing
    criteria.forEach((c) => {
      const icon = c.check ? checkMark : crossMark;
      const text = lang === "ENG" ? c.eng_text : c.thai_text;
      message += `<span style="filter: brightness(0) invert(1);">${icon} ${text}</span><br/>`;
    });
  }

  return { passed, message };
}

/**
 * Validates a password against certain criteria.
 * Returns an empty string if all checks pass, otherwise returns the error message.
 *
 * @param {string} password - The password to validate
 * @param {string} language - "ENG" or another language code
 * @returns {string} Validation message or empty string if passed
 */
function validateADPassword(password, language) {
  const warning = "&#8252;";
  const space = "&#x20;";

  // Run all checks
  const lengthResult = checkLength(password, language);
  const spaceResult = checkSpace(password, language);
  const complexityResult = checkComplexity(password, language);

  // If all passed, return empty
  if (lengthResult.passed && spaceResult.passed && complexityResult.passed) {
    return "";
  }

  // Otherwise, construct error message
  const failHeading =
    language === "ENG"
      ? `<span style="color: #ffffff;"><b>${warning}</b> ${space} Password failed to meet the requirements.</span></br>`
      : `<span style="color: #ffffff;"><b>${warning}</b> ${space} รหัสผ่านไม่ตรงตามข้อกำหนด</span></br>`;

  let resultMessage = failHeading;

  // Add each failed or all messages
  // Since the original code always adds all checks, we replicate that behavior:
  resultMessage += lengthResult.message;
  resultMessage += spaceResult.message;
  resultMessage += complexityResult.message;

  return resultMessage;
}

export { validateADPassword };
